import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    ShieldCheck,
    Camera,
    User,
    Briefcase,
    Megaphone,
    IdCard,
    ImagePlus,
    Check,
    Clock,
    ArrowRight,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, GradientButton } from "@/screens/_shared";
import { fileToImageDataUrl } from "@/lib/hooks";
import { addTransaction } from "@/lib/monetizationStore";
import PaymentSheet from "@/components/PaymentSheet";

/**
 * One self-contained verification flow — step-based:
 *  intro -> type -> idType -> uploadId -> selfie -> pay -> pending
 * After 2 mins (from submit), verification is auto-approved.
 */

const FEE = 3000;
const AUTO_APPROVE_MS = 2 * 60 * 1000;

const TYPES = [
    { id: "personal", label: "Personal",          icon: User },
    { id: "provider", label: "Service Provider",  icon: Briefcase },
    { id: "host",     label: "Event Host",        icon: Megaphone },
];
const ID_TYPES = [
    { id: "passport", label: "Passport" },
    { id: "license",  label: "Driver's License" },
    { id: "nin",      label: "National ID (NIN)" },
];
const SELFIE_STEPS = [
    { id: "left",    label: "Look left" },
    { id: "right",   label: "Look right" },
    { id: "forward", label: "Face forward" },
];

const Verification = () => {
    const navigate = useNavigate();
    const { user, updateUser } = useAuth();

    const existing = user?.verification;
    const initialStep = existing?.status === "pending" ? "pending"
        : existing?.status === "approved" ? "approved"
        : "intro";
    const [step, setStep] = useState(initialStep);
    const [type, setType] = useState(existing?.type || "personal");
    const [idType, setIdType] = useState(existing?.idType || "");
    const [idImage, setIdImage] = useState(null);
    const [selfieIdx, setSelfieIdx] = useState(0);
    const [capturedSelfie, setCapturedSelfie] = useState(null);
    const [busy, setBusy] = useState(false);
    const [payOpen, setPayOpen] = useState(false);
    const fileRef = useRef(null);
    const selfieFileRef = useRef(null);

    // Poll for auto-approval when pending
    useEffect(() => {
        if (step !== "pending") return;
        const submittedAt = user?.verification?.submittedAt
            ? new Date(user.verification.submittedAt).getTime()
            : Date.now();
        const elapsed = Date.now() - submittedAt;
        if (elapsed >= AUTO_APPROVE_MS) {
            approveNow();
            return;
        }
        const remaining = AUTO_APPROVE_MS - elapsed;
        const t = setTimeout(approveNow, remaining);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [step]);

    const approveNow = () => {
        updateUser({
            verified: true,
            verification: {
                ...(user?.verification || {}),
                status: "approved",
                approvedAt: new Date().toISOString(),
            },
        });
        setStep("approved");
    };

    const pickIdImage = async (file) => {
        if (!file) return;
        setBusy(true);
        try {
            const url = await fileToImageDataUrl(file, 1200, 0.78);
            setIdImage(url);
        } finally {
            setBusy(false);
        }
    };

    const pickSelfie = async (file) => {
        if (!file) return;
        setBusy(true);
        try {
            const url = await fileToImageDataUrl(file, 800, 0.78);
            setCapturedSelfie(url);
        } finally {
            setBusy(false);
        }
    };

    const handleSelfieNext = () => {
        if (!capturedSelfie) return;
        if (selfieIdx < SELFIE_STEPS.length - 1) {
            setSelfieIdx((i) => i + 1);
            setCapturedSelfie(null);
        } else {
            setStep("pay");
        }
    };

    const handlePay = () => {
        // Open Paystack-style payment sheet
        setPayOpen(true);
    };

    const handlePaymentSuccess = () => {
        const now = new Date().toISOString();
        updateUser({
            verification: {
                status: "pending",
                type,
                idType,
                submittedAt: now,
            },
        });
        if (user) {
            addTransaction(user.id, {
                type: "debit",
                kind: "verification",
                title: "Verification fee",
                amount: FEE,
            });
        }
        setPayOpen(false);
        setStep("pending");
    };

    return (
        <div className="screen" data-testid="verification-screen">
            <PageHeader title="Verification" />
            <div className="flex-1 overflow-y-auto no-scrollbar px-5 py-5">
                {step === "intro" && (
                    <Intro
                        onStart={() => setStep("type")}
                        status={existing?.status}
                    />
                )}
                {step === "type" && (
                    <ChoiceList
                        testId="verification-type"
                        title="Who are you verifying as?"
                        items={TYPES.map((t) => ({ id: t.id, label: t.label, icon: t.icon }))}
                        value={type}
                        onChange={(v) => setType(v)}
                        onNext={() => setStep("idType")}
                    />
                )}
                {step === "idType" && (
                    <ChoiceList
                        testId="verification-idtype"
                        title="Select ID type"
                        items={ID_TYPES.map((t) => ({ id: t.id, label: t.label, icon: IdCard }))}
                        value={idType}
                        onChange={(v) => setIdType(v)}
                        onNext={() => idType && setStep("uploadId")}
                        nextDisabled={!idType}
                    />
                )}
                {step === "uploadId" && (
                    <UploadId
                        image={idImage}
                        onPick={() => fileRef.current?.click()}
                        onRemove={() => setIdImage(null)}
                        inputRef={fileRef}
                        onFile={pickIdImage}
                        busy={busy}
                        onNext={() => idImage && setStep("selfie")}
                    />
                )}
                {step === "selfie" && (
                    <Selfie
                        stepLabel={SELFIE_STEPS[selfieIdx].label}
                        stepIdx={selfieIdx}
                        total={SELFIE_STEPS.length}
                        capturedSelfie={capturedSelfie}
                        onOpen={() => selfieFileRef.current?.click()}
                        inputRef={selfieFileRef}
                        onFile={pickSelfie}
                        busy={busy}
                        onNext={handleSelfieNext}
                    />
                )}
                {step === "pay" && (
                    <PayStep fee={FEE} busy={busy} onPay={handlePay} />
                )}
                {step === "pending" && <Pending />}
                {step === "approved" && <Approved onDone={() => navigate("/profile")} />}
            </div>

            <PaymentSheet
                open={payOpen}
                onClose={() => setPayOpen(false)}
                onSuccess={handlePaymentSuccess}
                amount={FEE}
                title="Pay verification fee"
                purposeLabel="verification"
                summary={
                    <div className="flex items-center gap-3 p-3 rounded-2xl bg-muted/40 border border-border">
                        <span className="h-12 w-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center shrink-0">
                            <ShieldCheck size={20} />
                        </span>
                        <div className="min-w-0 flex-1">
                            <div className="font-display text-[14px] font-semibold truncate">
                                Verification fee
                            </div>
                            <div className="text-[11.5px] text-muted-foreground truncate">
                                One-time charge · auto-approved in 2 min
                            </div>
                        </div>
                    </div>
                }
            />
        </div>
    );
};

/* ---- sub-views ---- */

const Intro = ({ onStart, status }) => (
    <div data-testid="verification-intro">
        <div
            className="rounded-3xl p-5 text-primary-foreground shadow-card"
            style={{
                background:
                    "linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(var(--primary) / 0.72) 100%)",
            }}
        >
            <ShieldCheck size={28} strokeWidth={2.3} />
            <h2 className="mt-3 font-display text-[20px] font-semibold">Get verified on Neyra</h2>
            <p className="mt-1.5 text-[13px] text-white/85">
                Build trust, stand out in search, and win more bookings.
            </p>
        </div>
        <ul className="mt-5 space-y-2.5 text-[13.5px]">
            <BenefitLine text="Build trust with a verified badge" />
            <BenefitLine text="Increase visibility in Discover" />
            <BenefitLine text="Get more bookings and requests" />
        </ul>

        <div className="mt-5 rounded-2xl border border-border bg-card p-4">
            <div className="flex items-center justify-between">
                <span className="text-[13px] text-muted-foreground">Verification fee</span>
                <span className="font-display font-semibold">₦{FEE.toLocaleString()}</span>
            </div>
            <p className="mt-1.5 text-[11.5px] text-muted-foreground">
                Verification does not unlock contact access — only Premium does.
            </p>
        </div>

        {status === "pending" && (
            <div className="mt-4 rounded-2xl border border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-300 text-[12.5px] p-3">
                You have a pending verification. We'll auto-approve shortly.
            </div>
        )}

        <div className="mt-6">
            <GradientButton testId="verification-start-btn" onClick={onStart}>
                Get Verified
            </GradientButton>
        </div>
    </div>
);

const BenefitLine = ({ text }) => (
    <li className="flex items-center gap-2.5">
        <span className="h-6 w-6 rounded-full bg-primary/10 text-primary flex items-center justify-center">
            <Check size={13} strokeWidth={2.6} />
        </span>
        <span>{text}</span>
    </li>
);

const ChoiceList = ({ title, items, value, onChange, onNext, nextDisabled, testId }) => (
    <div data-testid={testId}>
        <h2 className="font-display text-[20px] font-semibold mb-3">{title}</h2>
        <div className="space-y-2.5">
            {items.map((it) => {
                const Icon = it.icon;
                const active = value === it.id;
                return (
                    <button
                        key={it.id}
                        data-testid={`${testId}-${it.id}`}
                        onClick={() => onChange(it.id)}
                        className={[
                            "w-full flex items-center gap-3 p-3.5 rounded-2xl border transition-[transform,border-color] active:scale-[0.99]",
                            active
                                ? "border-primary bg-primary/5 shadow-[0_0_0_3px_hsl(var(--primary)/0.15)]"
                                : "border-border bg-card",
                        ].join(" ")}
                    >
                        <span className="h-10 w-10 rounded-xl flex items-center justify-center bg-primary/10 text-primary">
                            <Icon size={18} strokeWidth={2.2} />
                        </span>
                        <span className="flex-1 text-left text-[14px] font-medium">{it.label}</span>
                        {active && <Check size={16} className="text-primary" />}
                    </button>
                );
            })}
        </div>
        <div className="mt-6">
            <GradientButton testId={`${testId}-next`} onClick={onNext} disabled={nextDisabled}>
                Continue <ArrowRight size={16} className="inline ml-1 -mt-0.5" />
            </GradientButton>
        </div>
    </div>
);

const UploadId = ({ image, onPick, onRemove, inputRef, onFile, busy, onNext }) => (
    <div data-testid="verification-upload">
        <h2 className="font-display text-[20px] font-semibold mb-1.5">Upload your ID</h2>
        <p className="text-[13px] text-muted-foreground mb-4">
            A clear photo of the front of your document.
        </p>
        <div
            className="aspect-[16/10] rounded-2xl border-2 border-dashed border-border bg-secondary flex items-center justify-center overflow-hidden"
            onClick={onPick}
            role="button"
            data-testid="verification-upload-dropzone"
        >
            {image ? (
                <img src={image} alt="" className="h-full w-full object-cover" />
            ) : (
                <div className="flex flex-col items-center gap-1 text-muted-foreground">
                    <ImagePlus size={22} />
                    <span className="text-[12.5px] font-medium">Tap to upload</span>
                </div>
            )}
        </div>
        <input
            type="file"
            accept="image/*"
            ref={inputRef}
            className="hidden"
            data-testid="verification-upload-input"
            onChange={(e) => onFile(e.target.files?.[0])}
        />
        {image && (
            <button
                onClick={onRemove}
                data-testid="verification-upload-remove"
                className="mt-2 text-[12.5px] text-destructive font-medium"
            >
                Remove
            </button>
        )}
        <div className="mt-6">
            <GradientButton
                testId="verification-upload-next"
                onClick={onNext}
                disabled={!image || busy}
            >
                Continue
            </GradientButton>
        </div>
    </div>
);

const Selfie = ({ stepLabel, stepIdx, total, capturedSelfie, onOpen, inputRef, onFile, busy, onNext }) => (
    <div data-testid="verification-selfie">
        <h2 className="font-display text-[20px] font-semibold mb-1.5">Selfie verification</h2>
        <p className="text-[13px] text-muted-foreground mb-4">
            Step {stepIdx + 1} of {total} — <span className="text-foreground font-medium">{stepLabel}</span>
        </p>
        <div
            className="aspect-square rounded-3xl border-2 border-dashed border-border bg-secondary flex items-center justify-center overflow-hidden"
            onClick={onOpen}
            role="button"
            data-testid="verification-selfie-dropzone"
        >
            {capturedSelfie ? (
                <img src={capturedSelfie} alt="" className="h-full w-full object-cover" />
            ) : (
                <div className="flex flex-col items-center gap-2 text-muted-foreground">
                    <span className="h-14 w-14 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                        <Camera size={22} />
                    </span>
                    <span className="text-[12.5px] font-medium">Tap to capture</span>
                </div>
            )}
        </div>
        <input
            type="file"
            accept="image/*"
            capture="user"
            ref={inputRef}
            className="hidden"
            data-testid="verification-selfie-input"
            onChange={(e) => onFile(e.target.files?.[0])}
        />
        <div className="mt-6">
            <GradientButton
                testId="verification-selfie-next"
                onClick={onNext}
                disabled={!capturedSelfie || busy}
            >
                {stepIdx < total - 1 ? "Next pose" : "Continue"}
            </GradientButton>
        </div>
    </div>
);

const PayStep = ({ fee, busy, onPay }) => (
    <div data-testid="verification-pay">
        <h2 className="font-display text-[20px] font-semibold mb-1.5">Pay verification fee</h2>
        <p className="text-[13px] text-muted-foreground mb-4">
            Secure Paystack checkout — Card or Bank Transfer.
        </p>
        <div className="rounded-2xl border border-border bg-card p-4 flex items-center gap-3">
            <span className="h-10 w-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                <ShieldCheck size={18} />
            </span>
            <div>
                <div className="text-[13.5px] font-medium">Verification fee</div>
                <div className="text-[11.5px] text-muted-foreground">One-time charge</div>
            </div>
            <div className="ml-auto font-display font-semibold">₦{fee.toLocaleString()}</div>
        </div>
        <div className="mt-6">
            <GradientButton testId="verification-pay-btn" onClick={onPay} disabled={busy}>
                {busy ? "Processing…" : `Pay ₦${fee.toLocaleString()}`}
            </GradientButton>
        </div>
    </div>
);

const Pending = () => (
    <div className="text-center pt-6" data-testid="verification-pending">
        <span className="mx-auto h-16 w-16 rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-300 flex items-center justify-center">
            <Clock size={28} />
        </span>
        <h2 className="mt-4 font-display text-[20px] font-semibold">Verification pending</h2>
        <p className="mt-1 text-[13px] text-muted-foreground max-w-xs mx-auto">
            We'll auto-approve your submission within 2 minutes. Feel free to keep using Neyra.
        </p>
    </div>
);

const Approved = ({ onDone }) => (
    <div className="text-center pt-6" data-testid="verification-approved">
        <span
            className="mx-auto h-16 w-16 rounded-full flex items-center justify-center text-primary-foreground"
            style={{ background: "linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.78))" }}
        >
            <ShieldCheck size={28} strokeWidth={2.4} />
        </span>
        <h2 className="mt-4 font-display text-[20px] font-semibold">You're verified 🎉</h2>
        <p className="mt-1 text-[13px] text-muted-foreground max-w-xs mx-auto">
            Your verified badge is now live on your profile.
        </p>
        <div className="mt-6">
            <GradientButton testId="verification-done-btn" onClick={onDone}>
                Back to profile
            </GradientButton>
        </div>
    </div>
);

export default Verification;
